/** Normal-chat streaming policy and shared viewport helpers.
 * Work activity follows the bounded controller in work_scroll_follower.js.
 */

export const WORK_STREAM_SCROLL = {
  TAIL: 'tail',
  HOLD: 'hold',
};

/**
 * The reply that is currently being written, if any. Empty drafts are
 * ignored: a placeholder row with no text is not yet something to align to.
 */
export function findStreamingReply(messages) {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message?.role !== 'assistant') continue;
    if (!(message.isStreaming || message.isTyping || message.isThinking)) continue;
    const content = String(message.content || '');
    const thinking = String(message.thinkingContent || '');
    if (!content.trim() && !thinking.trim()) continue;
    return { id: message.id || message._id || null, length: content.length + thinking.length };
  }
  return null;
}

/** Normal chat holds text in place unless the reader explicitly follows. */
export function getStreamScrollMode({ hasStreamingText, followLive } = {}) {
  return hasStreamingText && !followLive ? WORK_STREAM_SCROLL.HOLD : WORK_STREAM_SCROLL.TAIL;
}

/**
 * Studio open remounts / reflows the chat column as stills and clips land.
 * RN Web FlatList then snaps scrollTop to 0. Restore the last good offset
 * unless the user is navigating the thread — especially scrolling down from
 * the top. Restoring during that gesture pins them on older rows and the
 * list never grows far enough to reach the latest messages.
 */
export function shouldRestoreStudioScroll({
  previousOffset = 0,
  nextOffset = 0,
  userScrolling = false,
  userIntentUntil = 0,
  now = Date.now(),
  lastDownwardAt = 0,
  pinningToBottom = false,
} = {}) {
  if (userScrolling) return false;
  if (pinningToBottom) return false;
  if (now < Number(userIntentUntil || 0)) return false;
  if (now - Number(lastDownwardAt || 0) < 2500) return false;
  if (previousOffset < 80) return false;
  return previousOffset > 80 && nextOffset < 32;
}

/**
 * Jump-to-live / scroll-to-bottom is normally toggled from scroll events.
 * In-place streams grow content without moving scrollTop, so the button must
 * also be derived from content-size changes or it stays invisible.
 */
export function shouldShowScrollToBottomButton({
  contentHeight = 0,
  layoutHeight = 0,
  scrollTop = 0,
  bottomThreshold = 100,
} = {}) {
  const canScroll = contentHeight > layoutHeight + 20;
  if (!canScroll) return false;
  return scrollTop + layoutHeight < contentHeight - bottomThreshold;
}
